/** @jsxImportSource theme-ui */
import { Formik } from 'formik'
import Link from 'next/link'
import React from 'react'
import { Box, Button, Card, Flex, Input, Spinner, Text } from 'theme-ui'
import * as Yup from 'yup'
import { signIn } from 'next-auth/react'
import toast from 'react-hot-toast'
import { recoverCustomer } from '~/api/shopify-account'

type Props = {
  setActiveForm: (value: string) => void
}

function ResetForm({ setActiveForm }: Props) {
  const onResetPassword = async (values: any) => {
    const data: any = await fetch(`/api/shopify/customer/get-customer`, {
      method: 'POST',
      headers: {
        'Content-type': 'application/json',
      },
      body: JSON.stringify({
        email: values.email,
      }),
    }).then((res) => res.json())
    const customer = data.customer
    if (customer == null) {
      toast.error('No email found')
      return
    }
    if (customer?.state != 'ENABLED') {
      const res: any = await fetch(
        `/api/shopify/customer/generate-activate-url`,
        {
          method: 'POST',
          headers: {
            'Content-type': 'application/json',
          },
          body: JSON.stringify({
            id: customer?.id,
          }),
        }
      ).then((res) => res.json())
      if (res.success) {
        toast.success(
          'Account not activated. Please check your email for activation link'
        )
        return
      } else {
        toast.error('Account not activated. Please contact support')
        return
      }
    } else {
      const response: any = await recoverCustomer(values.email)
      if (response.error) {
        toast.error(response.error)
        return
      }
      toast.success('Password reset email sent')
    }
  }

  return (
    <Flex sx={{ flexDirection: 'column' }}>
      <Text
        sx={{
          fontSize: [5, 6],
          textAlign: 'center',
          mb: 4,
          width: '100%',
        }}
      >
        Reset Password
      </Text>
      <Card
        sx={{
          px: [3, 5],
          py: [4, 6],
          boxShadow: 'header',
          bg: 'background',
          borderRadius: 3,
        }}
      >
        <Formik
          initialValues={{ email: '' }}
          validationSchema={Yup.object({
            email: Yup.string()
              .email('Invalid email address')
              .required('Required'),
          })}
          onSubmit={onResetPassword}
        >
          {(formik: any) => (
            <form onSubmit={formik.handleSubmit} style={{ marginBottom: 0 }}>
              <Box>
                <Flex sx={{ display: ['block'] }}>
                  <Box sx={{ marginBottom: 3, flex: [null, 1] }}>
                    <Input
                      type="Email"
                      placeholder="Email Address"
                      {...formik.getFieldProps('email')}
                      sx={{ borderColor: 'border' }}
                    />
                  </Box>
                </Flex>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {formik.isSubmitting && <Spinner />}
                {!formik.isSubmitting && (
                  <Button
                    variant="primary"
                    type="submit"
                    sx={{
                      borderRadius: (t: any) => t.radii[3],
                      width: '100%',
                    }}
                    disabled={formik.isSubmitting}
                  >
                    Reset Password
                  </Button>
                )}
              </Box>
              <Box>
                <Flex
                  sx={{
                    mt: 3,
                    justifyContent: 'space-between',
                  }}
                >
                  <Link
                    href="/account/register"
                    style={{ textDecoration: 'none' }}
                  >
                    <Text
                      sx={{
                        fontSize: 1,
                        color: 'text',
                        textAlign: 'center',
                        width: '100%',
                        cursor: 'pointer',
                      }}
                    >
                      Create account
                    </Text>
                  </Link>
                  <Link
                    href="#"
                    style={{ textDecoration: 'none' }}
                    onClick={() => {
                      setActiveForm('login')
                    }}
                  >
                    <Text
                      sx={{
                        fontSize: 1,
                        color: 'text',
                        textAlign: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      Remember your password?
                    </Text>
                  </Link>
                </Flex>
              </Box>
            </form>
          )}
        </Formik>
      </Card>
    </Flex>
  )
}

export default ResetForm
